/**
 * CalendarEventDialog - Diálogo para registrar Falta ou Feriado no calendário
 *
 * Função:
 * - Permite que proprietário/gerente registre eventos administrativos:
 *   - "falta": trabalhador não compareceu (impacta produção dele, não da propriedade)
 *   - "feriado": dia comemorativo (apenas etiqueta no calendário, sem impacto na contagem)
 *
 * Importante: estes eventos NÃO alteram a contagem de sangrias do sistema.
 * Servem apenas como contexto visual e informativo nos relatórios.
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { UserX, Flag, Info, Trash2 } from "lucide-react";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarEventDialog = ({
  open,
  onClose,
  property,
  date,
  workers = [], // lista de nomes de trabalhadores já cadastrados (sugestões)
  existingEvents = [], // eventos já cadastrados para esta data
  onSaved
}) => {
  const { t } = useTranslation();
  const [eventType, setEventType] = useState("falta"); // "falta" | "feriado"
  const [trabalhador, setTrabalhador] = useState("");
  const [motivo, setMotivo] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Resetar campos ao abrir
  useEffect(() => {
    if (open) {
      setEventType("falta");
      setTrabalhador("");
      setMotivo("");
      setDescription("");
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validações
    if (eventType === "falta" && !trabalhador.trim()) {
      setError(t("celFaltaWorkerRequired") || "Selecione ou digite o nome do trabalhador.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        property_id: property.id,
        date,
        event_type: eventType,
        ...(eventType === "falta" && {
          trabalhador_responsavel: trabalhador.trim(),
          motivo: motivo.trim() || null
        }),
        ...(eventType === "feriado" && {
          description: description.trim() || null
        })
      };

      await axios.post(`${API}/calendar-events`, payload);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || (t("celSaveError") || "Erro ao salvar evento."));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm(t("celConfirmDelete") || "Remover este evento?")) return;
    try {
      await axios.delete(`${API}/calendar-events/${eventId}`);
      onSaved && onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || (t("celDeleteError") || "Erro ao remover evento."));
    }
  };

  if (!open) return null;

  const dateLabel = date
    ? new Date(date + "T00:00:00").toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric"
      })
    : "";

  // Eventos existentes para esta data (faltas + feriado)
  const dayFaltas = existingEvents.filter((e) => e.event_type === "falta");
  const dayFeriado = existingEvents.find((e) => e.event_type === "feriado");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="calendar-event-dialog">
        <DialogHeader>
          <DialogTitle>{t("celTitle") || "Falta ou Feriado"}</DialogTitle>
          <DialogDescription>
            <span className="capitalize">{dateLabel}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Caixa explicativa */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2 mb-2">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-800 leading-relaxed">
            {t("celExplain") ||
              "Faltas e feriados são etiquetas informativas — não alteram a contagem de sangrias do sistema. Servem para dar contexto nos relatórios."}
          </p>
        </div>

        {/* Eventos já cadastrados nesta data */}
        {(dayFaltas.length > 0 || dayFeriado) && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
            <p className="text-xs font-semibold text-gray-700">
              {t("celExistingTitle") || "Eventos deste dia:"}
            </p>
            {dayFeriado && (
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded p-2">
                <div className="flex items-center gap-2 text-sm">
                  <Flag className="w-4 h-4 text-gray-600" />
                  <span className="font-medium">{t("celHoliday") || "Feriado"}</span>
                  {dayFeriado.description && (
                    <span className="text-gray-600">— {dayFeriado.description}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(dayFeriado.id)}
                  className="text-red-500 hover:text-red-700"
                  data-testid="delete-holiday-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
            {dayFaltas.map((f) => (
              <div
                key={f.id}
                className="flex items-center justify-between bg-white border border-gray-200 rounded p-2"
              >
                <div className="flex items-center gap-2 text-sm">
                  <UserX className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">{t("celAbsence") || "Falta"}:</span>
                  <span>{f.trabalhador_responsavel}</span>
                  {f.motivo && <span className="text-gray-500 text-xs">({f.motivo})</span>}
                  {f.compensada && (
                    <span className="text-xs text-green-700 font-medium ml-1">
                      ✓ {t("celCompensated") || "compensada"}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(f.id)}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`delete-absence-btn-${f.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Seletor de tipo */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setEventType("falta")}
              data-testid="event-type-falta"
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                eventType === "falta"
                  ? "border-amber-500 bg-amber-50 text-amber-900"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              <UserX className="w-5 h-5 mb-1" />
              <span className="text-sm font-medium">{t("celAbsence") || "Falta"}</span>
            </button>
            <button
              type="button"
              onClick={() => setEventType("feriado")}
              data-testid="event-type-feriado"
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                eventType === "feriado"
                  ? "border-gray-500 bg-gray-100 text-gray-900"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              <Flag className="w-5 h-5 mb-1" />
              <span className="text-sm font-medium">{t("celHoliday") || "Feriado"}</span>
            </button>
          </div>

          {/* Campos da Falta */}
          {eventType === "falta" && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="trabalhador">
                  {t("celWorker") || "Trabalhador"} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="trabalhador"
                  list="workers-list"
                  value={trabalhador}
                  onChange={(e) => setTrabalhador(e.target.value)}
                  placeholder={t("celWorkerPlaceholder") || "Nome do trabalhador"}
                  data-testid="absence-worker-input"
                  required
                />
                {workers.length > 0 && (
                  <datalist id="workers-list">
                    {workers.map((w) => (
                      <option key={w} value={w} />
                    ))}
                  </datalist>
                )}
              </div>
              <div>
                <Label htmlFor="motivo">
                  {t("celReason") || "Motivo"}{" "}
                  <span className="text-gray-400 text-xs">({t("optional") || "opcional"})</span>
                </Label>
                <Input
                  id="motivo"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder={t("celReasonPlaceholder") || "Ex: Atestado médico, Particular"}
                  data-testid="absence-reason-input"
                  maxLength={120}
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
                💡{" "}
                {t("celAbsenceHint") ||
                  "Se este trabalhador depois compensar a falta em outra data (trabalhando num sábado, por exemplo), você poderá vincular essa sangria a esta falta no formulário de registro."}
              </div>
            </div>
          )}

          {/* Campos do Feriado */}
          {eventType === "feriado" && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="description">
                  {t("celHolidayName") || "Nome do feriado"}{" "}
                  <span className="text-gray-400 text-xs">({t("optional") || "opcional"})</span>
                </Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("celHolidayPlaceholder") || "Ex: Carnaval, Natal, Aniversário da cidade"}
                  data-testid="holiday-name-input"
                  maxLength={80}
                />
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs text-gray-700">
                💡{" "}
                {t("celHolidayHint") ||
                  "O feriado é apenas uma etiqueta no calendário. Se algum trabalhador escolher trabalhar neste dia (parceria), a sangria registrada conta normalmente."}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-event-btn">
              {t("cancel") || "Cancelar"}
            </Button>
            <Button type="submit" disabled={loading} data-testid="save-event-btn">
              {loading ? (t("saving") || "Salvando...") : (t("save") || "Salvar")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarEventDialog;
